import { useState, useRef, useEffect } from 'react';
import { Brain, Sparkles, RefreshCw, AlertCircle, Send, Download, Copy, Check } from '../icons';
import { MarkdownRenderer } from './MarkdownRenderer';
import { getQuickTopics, getFullReportPrompt, getContinuationPrompt, type Locale } from './constants';
import { buildAIPrompt } from './buildAIPrompt';
import { supabase } from '../../lib/supabase';
import type { Score } from '../../types';
import type { HarmonicScore } from '../../types/harmonic';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AIAnalysisPanelProps {
  analysis: string | undefined;
  scores: Score[];
  harmonicScores: HarmonicScore[];
  resultId: string;
  showRegenerate?: boolean;
  respondentName?: string;
  locale?: Locale;
  storageField?: 'ai_analysis' | 'coach_ai_analysis';
  onAnalysisUpdate: (analysis: string) => void;
}

const CONTINUATION_MARKER = 'Continue generating Part';
const MAX_CONTINUATIONS = 4;

export function AIAnalysisPanel({
  analysis,
  scores,
  harmonicScores,
  resultId,
  showRegenerate = false,
  respondentName,
  locale = 'en',
  storageField = 'ai_analysis',
  onAnalysisUpdate,
}: AIAnalysisPanelProps) {
  const tr = locale === 'tr';
  const quickTopics = getQuickTopics(locale);
  const localizedInstruction = (instruction: string) => tr
    ? `${instruction}\n\nYanıtın tamamını açık, profesyonel ve doğal Türkçe ile yaz.`
    : instruction;

  const [isGenerating, setIsGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [copied, setCopied] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const analysisRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (analysis && messages.length === 0) {
      setMessages([{
        id: 'initial-analysis',
        role: 'assistant',
        content: analysis,
        timestamp: new Date(),
      }]);
    }
  }, [analysis]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getUserId = async (): Promise<string | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  };

  const callGenerateAnalysis = async (
    prompt: string,
    conversationHistory?: { role: string; content: string }[],
    userId?: string | null
  ) => {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        prompt,
        conversation_history: conversationHistory,
        result_id: resultId,
        user_id: userId,
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to generate analysis');
    }

    return response.json();
  };

  const needsContinuation = (text: string): boolean => {
    const lastLines = text.slice(-200).toLowerCase();
    return lastLines.includes('continue generating part') ||
           lastLines.includes('continued in part') ||
           lastLines.includes('end of part');
  };

  const sendFullReport = async (instruction: string, isInitialGeneration: boolean) => {
    const prompt = buildAIPrompt(scores, harmonicScores, localizedInstruction(instruction), locale);
    setIsGenerating(true);
    setAiError(null);
    setGenerationProgress(tr ? 'Rapor oluşturuluyor (Bölüm 1)...' : 'Generating report (Part 1)...');

    try {
      const userId = await getUserId();
      let fullContent = '';
      let continuationCount = 0;
      let conversationHistory: { role: string; content: string }[] = [];

      // First call
      const firstData = await callGenerateAnalysis(prompt, undefined, userId);
      fullContent = firstData.analysis;
      conversationHistory = [
        { role: 'user', content: prompt },
        { role: 'assistant', content: fullContent },
      ];

      // Check if continuation is needed
      while (needsContinuation(fullContent) && continuationCount < MAX_CONTINUATIONS) {
        continuationCount++;
        setGenerationProgress(tr ? `Rapor oluşturuluyor (Bölüm ${continuationCount + 1})...` : `Generating report (Part ${continuationCount + 1})...`);

        const continuationPrompt = buildAIPrompt(
          scores,
          harmonicScores,
          getContinuationPrompt(locale, continuationCount + 2),
          locale
        );

        const contData = await callGenerateAnalysis(
          continuationPrompt,
          conversationHistory,
          userId
        );

        fullContent += '\n\n' + contData.analysis;
        conversationHistory.push(
          { role: 'user', content: continuationPrompt },
          { role: 'assistant', content: contData.analysis },
        );
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: fullContent,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (isInitialGeneration) {
        const { error: updateError } = await supabase
          .from('results')
          .update({
            [storageField]: fullContent,
            [`${storageField}_generated_at`]: new Date().toISOString()
          })
          .eq('id', resultId);

        if (updateError) {
          console.error('Failed to save analysis:', updateError.message);
        }

        onAnalysisUpdate(fullContent);
      }
    } catch (err) {
      console.error('Error generating analysis:', err);
      setAiError((err as Error).message || 'Failed to generate AI analysis. Please try again.');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(null);
    }
  };

  const sendMessage = async (instruction: string, isInitialGeneration = false) => {
    const prompt = buildAIPrompt(scores, harmonicScores, localizedInstruction(instruction), locale);

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: instruction,
      timestamp: new Date(),
    };

    if (!isInitialGeneration) {
      setMessages(prev => [...prev, userMessage]);
    }

    setIsGenerating(true);
    setAiError(null);

    try {
      const userId = await getUserId();

      const conversationHistory = messages
        .filter(m => m.id !== 'initial-analysis')
        .map(m => ({ role: m.role, content: m.content }));

      const data = await callGenerateAnalysis(
        prompt,
        isInitialGeneration ? undefined : conversationHistory,
        userId
      );

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.analysis,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (isInitialGeneration) {
        const { error: updateError } = await supabase
          .from('results')
          .update({
            [storageField]: data.analysis,
            [`${storageField}_generated_at`]: new Date().toISOString()
          })
          .eq('id', resultId);

        if (updateError) {
          console.error('Failed to save analysis:', updateError.message);
        }

        onAnalysisUpdate(data.analysis);
      }
    } catch (err) {
      console.error('Error generating analysis:', err);
      setAiError((err as Error).message || 'Failed to generate AI analysis. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleQuickTopic = (topic: typeof quickTopics[0]) => {
    const isFullReport = 'isFullReport' in topic && topic.isFullReport;
    if (isFullReport) {
      sendFullReport(topic.prompt, messages.length === 0);
    } else {
      sendMessage(topic.prompt, messages.length === 0);
    }
  };

  const handleChatSubmit = () => {
    if (!chatInput.trim() || isGenerating) return;
    const instruction = chatInput.trim();
    setChatInput('');
    sendMessage(instruction, messages.length === 0);
  };

  const handleRegenerate = () => {
    setMessages([]);
    sendFullReport(getFullReportPrompt(locale), true);
  };

  const handleCopy = async () => {
    const allAssistant = messages.filter(m => m.role === 'assistant').map(m => m.content).join('\n\n');
    if (!allAssistant) return;
    await navigator.clipboard.writeText(allAssistant);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportPDF = async () => {
    if (!analysisRef.current) return;
    const html2pdf = (await import('html2pdf.js')).default;
    html2pdf()
      .set({
        margin: [15, 15, 15, 15],
        filename: `AI-Analysis${respondentName ? `-${respondentName}` : ''}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      })
      .from(analysisRef.current)
      .save();
  };

  const hasMessages = messages.length > 0;
  const hasAssistantMessage = messages.some(m => m.role === 'assistant');

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        {/* Quick Topics Sidebar */}
        <div className="freud-card-elevated p-5 flex flex-col">
          <h3 className="text-sm font-bold text-chocolate-900 mb-3 uppercase tracking-wide">{tr ? 'Hızlı Analiz' : 'Quick Analysis'}</h3>
          <div className="flex flex-col gap-2 flex-1">
            {quickTopics.map((topic) => (
              <button
                key={topic.label}
                onClick={() => handleQuickTopic(topic)}
                disabled={isGenerating}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold text-left transition-all disabled:opacity-50 ${
                  'isFullReport' in topic && topic.isFullReport
                    ? 'bg-sage-600 text-white hover:bg-sage-700'
                    : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                }`}
              >
                {topic.label}
              </button>
            ))}
          </div>
        </div>

        {/* Conversation Area */}
        <div className="freud-card-elevated overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-cream-200">
            <div className="flex items-center gap-2.5">
              <Brain className="w-5 h-5 text-sage-600" />
              <h2 className="text-lg font-bold text-chocolate-900">{tr ? 'Yapay Zekâ Analizi' : 'AI Analysis'}</h2>
            </div>
            <div className="flex items-center gap-2">
              {showRegenerate && hasAssistantMessage && !isGenerating && (
                <button
                  onClick={handleRegenerate}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-sage-600 text-white rounded-xl text-sm font-semibold hover:bg-sage-700 transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  {tr ? 'Yeni Analiz' : 'New Analysis'}
                </button>
              )}
            </div>
          </div>

          {aiError && (
            <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{aiError}</p>
            </div>
          )}

          <div ref={analysisRef} className="px-6 py-4 overflow-y-auto max-h-[80vh] flex-1 space-y-4">
            {!hasMessages && !isGenerating ? (
              <div className="py-16 text-center">
                <Sparkles className="w-12 h-12 text-cream-400 mx-auto mb-4" />
                <p className="text-chocolate-500 mb-2">{tr ? 'Human Potential OS™ Yapay Zekâ Analizi' : 'Human Potential OS™ AI Analysis'}</p>
                <p className="text-sm text-chocolate-400 mb-6 max-w-md mx-auto">
                  {tr ? 'Tam bir rapor oluşturun veya hızlı analiz konularından birini seçin. Sonuçları daha ayrıntılı incelemek için takip soruları sorun.' : 'Generate a full report or select a quick analysis topic. Ask follow-up questions to go deeper into the results.'}
                </p>
                <button
                  onClick={() => sendFullReport(getFullReportPrompt(locale), true)}
                  className="freud-btn-primary inline-flex items-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  {tr ? 'Tam Rapor Oluştur' : 'Generate Full Report'}
                </button>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[90%] rounded-2xl px-5 py-3 ${
                      msg.role === 'user'
                        ? 'bg-sage-600 text-white'
                        : 'bg-cream-50 border border-cream-200'
                    }`}>
                      {msg.role === 'assistant' ? (
                        <MarkdownRenderer content={msg.content} />
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {isGenerating && (
                  <div className="flex justify-start">
                    <div className="bg-cream-50 border border-cream-200 rounded-2xl px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        {generationProgress && (
                          <span className="text-sm text-chocolate-500">{generationProgress}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Chat Input */}
          <div className="px-6 py-4 border-t border-cream-200">
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder={
                  hasMessages
                    ? (respondentName
                        ? (tr ? `${respondentName} adlı danışanın sonuçları hakkında daha fazla sor...` : `Ask more about ${respondentName}'s results...`)
                        : (tr ? 'Takip sorusu sorun...' : 'Ask a follow-up question...'))
                    : (tr ? 'Değerlendirme sonuçları hakkında soru sorun...' : 'Ask about the assessment results...')
                }
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleChatSubmit()}
                disabled={isGenerating}
                className="freud-input"
              />
              <button
                onClick={handleChatSubmit}
                disabled={isGenerating || !chatInput.trim()}
                className="p-3 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-all disabled:opacity-50 flex-shrink-0"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Export Options */}
      {hasAssistantMessage && (
        <div className="freud-card-elevated p-6">
          <h3 className="text-lg font-bold text-chocolate-900 mb-4">{tr ? 'Dışa Aktarma Seçenekleri' : 'Export Options'}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              onClick={handleExportPDF}
              disabled={isGenerating}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-chocolate-700 text-white rounded-xl font-semibold hover:bg-chocolate-800 transition-all disabled:opacity-40"
            >
              <Download className="w-5 h-5" />
              {tr ? 'PDF olarak dışa aktar' : 'Export as PDF'}
            </button>
            <button
              onClick={handleCopy}
              disabled={isGenerating}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-chocolate-500 text-white rounded-xl font-semibold hover:bg-chocolate-600 transition-all disabled:opacity-40"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              {copied ? (tr ? 'Kopyalandı!' : 'Copied!') : (tr ? 'Tüm Yanıtları Kopyala' : 'Copy All Responses')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
